import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useSnackbar } from 'react-simple-snackbar'
import {
  previewBulkExport,
  sendBulkExport,
  downloadBulkExportZip,
} from '../../api/index'
import { useAdminGuard } from '../../hooks/useRoleGuard'
import ConfirmModal from '../ConfirmModal/ConfirmModal'
import Spinner from '../Spinner/Spinner'
import styles from './EnvoiGroupe.module.css'

const MONTHS = [
  { value: 1, label: 'Janvier' },
  { value: 2, label: 'Février' },
  { value: 3, label: 'Mars' },
  { value: 4, label: 'Avril' },
  { value: 5, label: 'Mai' },
  { value: 6, label: 'Juin' },
  { value: 7, label: 'Juillet' },
  { value: 8, label: 'Août' },
  { value: 9, label: 'Septembre' },
  { value: 10, label: 'Octobre' },
  { value: 11, label: 'Novembre' },
  { value: 12, label: 'Décembre' },
]

const triggerBlobDownload = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.click()
  window.URL.revokeObjectURL(url)
}

const EnvoiGroupe = () => {
  const [openSnackbar] = useSnackbar()
  const openSnackbarRef = useRef(openSnackbar)
  openSnackbarRef.current = openSnackbar
  useAdminGuard()

  const now = new Date()
  const [month, setMonth] = useState(now.getMonth() + 1)
  const [year, setYear] = useState(now.getFullYear())
  const [email, setEmail] = useState('')
  const [preview, setPreview] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState('')
  const [sending, setSending] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [confirmSend, setConfirmSend] = useState(false)
  const [lastResult, setLastResult] = useState(null)

  const periodLabel = `${MONTHS.find((m) => m.value === month)?.label} ${year}`

  const loadPreview = useCallback(async () => {
    setPreviewLoading(true)
    setPreviewError('')
    setPreview(null)
    setLastResult(null)
    try {
      const { data } = await previewBulkExport({ month, year })
      setPreview(data)
    } catch (error) {
      setPreviewError(error?.response?.data?.message || 'Impossible de charger l\'aperçu')
    } finally {
      setPreviewLoading(false)
    }
  }, [month, year])

  useEffect(() => {
    loadPreview()
  }, [loadPreview])

  const handleSend = async () => {
    setSending(true)
    try {
      const { data } = await sendBulkExport({ month, year, email: email.trim() })
      setLastResult(data)
      setConfirmSend(false)
      openSnackbarRef.current(data.message || 'Envoi terminé')
    } catch (error) {
      openSnackbarRef.current(error?.response?.data?.message || 'Erreur lors de l\'envoi')
    } finally {
      setSending(false)
    }
  }

  const handleDownload = async (part) => {
    setDownloading(true)
    try {
      const { data, headers } = await downloadBulkExportZip({ month, year, part })
      const slug = `${year}_${String(month).padStart(2, '0')}`
      const totalParts = Number(headers['x-zip-total-parts']) || 1
      const currentPart = Number(headers['x-zip-current-part']) || part || 1
      const fileName = totalParts > 1
        ? `factures-coaches-${slug}-partie-${currentPart}-sur-${totalParts}.zip`
        : `factures-coaches-${slug}.zip`
      triggerBlobDownload(data, fileName)
      openSnackbarRef.current('Archive téléchargée')
    } catch (error) {
      openSnackbarRef.current(error?.response?.data?.message || 'Erreur téléchargement')
    } finally {
      setDownloading(false)
    }
  }

  const canSend = preview?.invoiceCount > 0 && email.trim() && !sending && !downloading

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <div>
          <h1>Envoi groupé</h1>
          <p>
            Transmettre toutes les factures coaches du mois à la société de facturation (archive ZIP).
          </p>
        </div>
      </div>

      <div className={styles.panel}>
        <h2>Période et destinataire</h2>
        <p className={styles.hint}>
          Sélectionnez le mois concerné et saisissez l&apos;adresse email de la ste de facturation.
          Les statuts d&apos;envoi aux coaches ne sont pas modifiés.
        </p>

        <div className={styles.formGrid}>
          <div className={styles.field}>
            <label htmlFor="bulk-month">Mois</label>
            <select
              id="bulk-month"
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              disabled={sending || downloading}
            >
              {MONTHS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>

          <div className={styles.field}>
            <label htmlFor="bulk-year">Année</label>
            <input
              id="bulk-year"
              type="number"
              min="2020"
              max="2100"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              disabled={sending || downloading}
            />
          </div>

          <div className={`${styles.field} ${styles.fieldWide}`}>
            <label htmlFor="bulk-email">Email destinataire *</label>
            <input
              id="bulk-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="facturation@societe-partenaire.tn"
              disabled={sending || downloading}
            />
          </div>
        </div>
      </div>

      <div className={styles.panel}>
        <h2>Aperçu</h2>

        {previewLoading ? (
          <div className={styles.loadingRow}>
            <Spinner />
            <span>Chargement de la campagne {periodLabel}…</span>
          </div>
        ) : previewError ? (
          <p className={styles.error}>{previewError}</p>
        ) : preview ? (
          <>
            <div className={styles.statsGrid}>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>Factures</span>
                <span className={styles.statValue}>{preview.invoiceCount}</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>ZIP estimé</span>
                <span className={styles.statValue}>~{preview.estimatedZipMb} Mo</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>Emails à envoyer</span>
                <span className={styles.statValue}>{preview.estimatedParts}</span>
              </div>
            </div>

            {preview.estimatedParts > 1 && (
              <p className={styles.warn}>
                Le volume dépasse ~22 Mo : l&apos;envoi sera découpé en {preview.estimatedParts} emails ZIP.
              </p>
            )}

            <div className={styles.actions}>
              <button
                type="button"
                className={styles.sendBtn}
                disabled={!canSend}
                onClick={() => setConfirmSend(true)}
              >
                Envoyer par email
              </button>
              <button
                type="button"
                className={styles.downloadBtn}
                disabled={!preview.invoiceCount || sending || downloading}
                onClick={() => handleDownload(1)}
              >
                {downloading ? 'Préparation…' : 'Télécharger ZIP'}
              </button>
              {preview.estimatedParts > 1 && (
                <span className={styles.hint}>
                  Téléchargement : partie 1/{preview.estimatedParts} (relancer pour les autres si besoin)
                </span>
              )}
            </div>

            {(sending || downloading) && (
              <p className={styles.progressNote}>
                Génération des PDF et création du ZIP en cours… Cela peut prendre 2 à 5 minutes.
              </p>
            )}
          </>
        ) : null}
      </div>

      {lastResult && (
        <div className={styles.resultPanel}>
          <h3>Envoi réussi</h3>
          <p>
            <strong>{lastResult.totalInvoices}</strong> facture(s) — période{' '}
            <strong>{lastResult.periodLabel}</strong> — destinataire{' '}
            <strong>{lastResult.to}</strong>
          </p>
          {lastResult.zipParts?.length > 1 && (
            <ul>
              {lastResult.zipParts.map((part) => (
                <li key={part.part}>
                  Partie {part.part} : {part.invoiceCount} facture(s), {part.sizeMb} Mo — {part.fileName}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <ConfirmModal
        open={confirmSend}
        variant="warning"
        title="Confirmer l'envoi groupé ?"
        message={`Envoyer ${preview?.invoiceCount || 0} facture(s) de ${periodLabel} à ${email.trim()} ?`}
        detail={
          preview?.estimatedParts > 1
            ? `${preview.estimatedParts} emails seront envoyés (ZIP découpé).`
            : 'Un seul email avec une archive ZIP sera envoyé.'
        }
        confirmLabel={sending ? 'Envoi en cours…' : 'Confirmer l\'envoi'}
        cancelLabel="Annuler"
        loading={sending}
        onConfirm={handleSend}
        onCancel={() => !sending && setConfirmSend(false)}
      />
    </div>
  )
}

export default EnvoiGroupe
